import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { useCompanyIdFromRoute } from '@/app/hooks/useCompanyIdFromRoute'
import {
  formatPanelDate,
  splitAmount,
} from '@/modules/shared/utils/panelInvoices'

interface Invoice {
  number: string
  price: string
  left_to_pay: string
  sell_date: string
  payment_date: string
  invoice_url?: string | null
}

interface PaymentData {
  status: string
  message: string
  data?: {
    invoices: Invoice[]
    sum: string
  }
}

type Props = {
  data: PaymentData
}

export const PaymentRequiredModal = ({ data }: Props) => {
  const navigate = useNavigate()
  const companyId = useCompanyIdFromRoute()
  const invoices = data?.data?.invoices ?? []
  const sum = data?.data?.sum
  const parsedSum = splitAmount(sum ?? '')

  // "Zapłać teraz" opens the oldest unpaid invoice PDF (it carries the bank
  // transfer details) — Manual companies pay by transfer, there is no online flow.
  const firstInvoiceUrl = invoices.find((inv) => inv.invoice_url)?.invoice_url

  // The my-subscription page lists these invoices with full details.
  const subscriptionPath = companyId
    ? `/et/company/${companyId}/my-subscription`
    : null

  // The company has no record in the external payment panel (panel returned 404).
  // There are no invoices to list, so show a dedicated message instead of an empty
  // invoice table.
  const notInPanel = data?.status === 'not_in_panel'

  // Keep the left sidebar above this modal's overlay and clickable while it's open,
  // so the user can still navigate / switch company rather than being hard-blocked.
  // See the `body.payment-required-open` rule in tailwind.css.
  React.useEffect(() => {
    document.body.classList.add('payment-required-open')
    return () => document.body.classList.remove('payment-required-open')
  }, [])

  return (
    <ModalLayout customZIndex={99999}>
      <div
        className="tw-flex tw-w-[440px] tw-flex-col tw-rounded-lg tw-bg-white"
        style={{
          boxShadow:
            '0px 20px 48px rgba(31, 41, 55, 0.14), 0px 4px 12px rgba(31, 41, 55, 0.08), 0px 0px 0px 1px rgba(31, 41, 55, 0.06)',
        }}
      >
        {/* TITLE SECTION */}
        <div className="tw-flex tw-items-start tw-justify-between tw-p-4">
          <div className="tw-flex tw-flex-col tw-gap-[3px]">
            <h2 className="tw-text-xl tw-font-semibold tw-leading-7 tw-text-[#1F2937]">
              {notInPanel
                ? 'Potrzebujesz dostępu do większej liczby baz?'
                : 'Niezapłacona faktura'}
            </h2>
            {!notInPanel && (
              <p className="tw-text-[13px] tw-leading-[18px] tw-text-[#6B7A99]">
                Wykryto zaległości w płatnościach.
              </p>
            )}
          </div>
        </div>

        {/* BODY */}
        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-pb-4">
          {notInPanel && (
            <>
              <p className="tw-px-2 tw-text-[13px] tw-leading-5 tw-text-[#1F2937]">
                Jeśli chcesz pracować z większą liczbą baz i korzystać z pełnych
                możliwości EasyTacho, skontaktuj się z naszym Działem Obsługi
                Klienta. Chętnie pomożemy dobrać odpowiednie rozwiązanie i
                rozszerzyć dostęp.
              </p>
              <div className="tw-mx-2 tw-mt-1 tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-bg-[#F4F6FC] tw-p-3">
                <span className="tw-text-[13px] tw-font-semibold tw-text-[#1F2937]">
                  Dział Obsługi Klienta
                </span>
                {[
                  { tel: '+48510887301', label: '+48 510 887 301' },
                  { tel: '+48510887302', label: '+48 510 887 302' },
                  { tel: '+48223797026', label: '+48 22 379 70 26' },
                ].map((phone) => (
                  <a
                    key={phone.tel}
                    href={`tel:${phone.tel}`}
                    className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-medium tw-text-[#2448BB] tw-no-underline hover:tw-underline"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M14.667 11.28v2a1.333 1.333 0 01-1.454 1.333 13.2 13.2 0 01-5.753-2.046 13 13 0 01-4-4A13.2 13.2 0 011.413 2.78 1.333 1.333 0 012.74 1.333h2a1.333 1.333 0 011.333 1.147c.084.64.24 1.268.466 1.873a1.333 1.333 0 01-.3 1.407l-.846.846a10.667 10.667 0 004 4l.846-.846a1.333 1.333 0 011.407-.3c.605.226 1.233.382 1.873.466a1.333 1.333 0 011.147 1.354z"
                        stroke="#2448BB"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {phone.label}
                  </a>
                ))}
                <a
                  href="mailto:dok@easytacho.pl"
                  className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-medium tw-text-[#2448BB] tw-no-underline hover:tw-underline"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2.667 2.667h10.666c.734 0 1.334.6 1.334 1.333v8c0 .733-.6 1.333-1.334 1.333H2.667A1.337 1.337 0 011.333 12V4c0-.733.6-1.333 1.334-1.333z"
                      stroke="#2448BB"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14.667 4L8 8.667 1.333 4"
                      stroke="#2448BB"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  dok@easytacho.pl
                </a>
              </div>
            </>
          )}
          {invoices.map((inv) => {
            const parsed = splitAmount(inv.left_to_pay)
            return (
              <div
                key={inv.number}
                className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-[#E5E6E6] tw-px-2 tw-pb-3"
              >
                {/* Left: invoice details */}
                <div className="tw-flex tw-flex-col tw-gap-[5px]">
                  <span className="tw-text-[13px] tw-font-semibold tw-leading-4 tw-tracking-[0.13px] tw-text-[#1F2937]">
                    {inv.number}
                  </span>
                  <div className="tw-flex tw-flex-col">
                    {/* Sell date */}
                    <div className="tw-flex tw-items-center tw-gap-1 tw-py-[2px]">
                      <svg
                        className="tw-opacity-80"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M5.333 1.333v1.334M10.667 1.333v1.334M2 6.667h12M3.333 2.667h9.334c.736 0 1.333.597 1.333 1.333v9.333c0 .737-.597 1.334-1.333 1.334H3.333A1.333 1.333 0 012 13.333V4c0-.736.597-1.333 1.333-1.333z"
                          stroke="#1F2937"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-[#1F2937] tw-opacity-80">
                        <span className="tw-font-medium">Wystawiono</span>
                        <span>{formatPanelDate(inv.sell_date)}</span>
                      </div>
                    </div>
                    {/* Payment date */}
                    <div className="tw-flex tw-items-center tw-gap-1 tw-py-[2px]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M5.333 1.333v1.334M10.667 1.333v1.334M2 6.667h12M3.333 2.667h9.334c.736 0 1.333.597 1.333 1.333v9.333c0 .737-.597 1.334-1.333 1.334H3.333A1.333 1.333 0 012 13.333V4c0-.736.597-1.333 1.333-1.333z"
                          stroke="#E03A3E"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-[#E03A3E]">
                        <span className="tw-font-medium">Data płatności</span>
                        <span>{formatPanelDate(inv.payment_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right: amount */}
                <div className="tw-flex tw-items-baseline tw-gap-[2px]">
                  <span className="tw-text-lg tw-font-semibold tw-leading-[18px] tw-text-[#1F2937]">
                    {parsed.amount}
                  </span>
                  {parsed.currency && (
                    <span className="tw-text-xs tw-text-[#1F2937] tw-opacity-80">
                      {parsed.currency}
                    </span>
                  )}
                </div>
              </div>
            )
          })}

          {/* Sum row */}
          {sum && (
            <div className="tw-flex tw-items-baseline tw-justify-between tw-px-2">
              <span className="tw-text-xs tw-font-medium tw-text-[#6B7A99]">
                Zaległość łącznie
              </span>
              <div className="tw-flex tw-items-baseline tw-gap-[2px]">
                <span className="tw-text-[15px] tw-font-bold tw-leading-[18px] tw-text-[#1F2937]">
                  {parsedSum.amount}
                </span>
                {parsedSum.currency && (
                  <span className="tw-text-xs tw-text-[#1F2937] tw-opacity-80">
                    {parsedSum.currency}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* DIVIDER */}
        {!notInPanel && <div className="tw-h-px tw-bg-[#E5E6E6]" />}

        {/* ACTIONS — there is nothing to pay when the company has no panel record */}
        {!notInPanel && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
            {firstInvoiceUrl && (
              <button
                type="button"
                onClick={() =>
                  window.open(firstInvoiceUrl, '_blank', 'noopener')
                }
                className="tw-flex tw-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-[#2448BB] tw-text-sm tw-font-medium tw-text-[#FAFBFF] hover:tw-opacity-90"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2 4.667C2 3.93 2.597 3.333 3.333 3.333h9.334C13.403 3.333 14 3.93 14 4.667v6.666c0 .737-.597 1.334-1.333 1.334H3.333A1.333 1.333 0 012 11.333V4.667z"
                    stroke="#FAFBFF"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M2 5.333l5.47 3.647a1 1 0 001.06 0L14 5.333"
                    stroke="#FAFBFF"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Zapłać teraz
              </button>
            )}
            {subscriptionPath && (
              <button
                type="button"
                onClick={() => navigate(subscriptionPath)}
                className="tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-[#E4E9F7] tw-text-sm tw-font-medium tw-text-[#2448BB] hover:tw-opacity-90"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 3h10M3.5 1v1.5M8.5 1v1.5M2.5 2h7a1 1 0 011 1v7a1 1 0 01-1 1h-7a1 1 0 01-1-1V3a1 1 0 011-1z"
                    stroke="#2448BB"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Zobacz płatności
              </button>
            )}
          </div>
        )}

        {/* FOOTER NOTE */}
        <div className="tw-flex tw-items-center tw-gap-[6px] tw-px-4 tw-pb-4">
          <svg
            className="tw-shrink-0 tw-opacity-80"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle cx="8" cy="8" r="7" stroke="#1F2937" strokeWidth="1.2" />
            <path
              d="M8 5v3M8 10.5v.5"
              stroke="#1F2937"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          <span className="tw-text-[11px] tw-leading-4 tw-text-[#1F2937] tw-opacity-80">
            Pamiętaj, że brak terminowej płatności może skutkować ograniczeniem
            dostępu do wybranych funkcji systemu.
          </span>
        </div>
      </div>
    </ModalLayout>
  )
}
